import React, { useState, useEffect } from "react";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getVehicleFilteringOptions,
  getClearVehicleModel,
} from "../../common/EntitiesManagement";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import {
  generateNoDataValues,
  GetDateInMMDDYYYYFormat,
  getFiltersInfo,
} from "../../common/utils";
import AddVehicle from "./components/AddVehicle";
import EditVehicle from "./components/EditVehicle";
import NoDataLabel from "../../components/common/NoDataLabel";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function Vehicle() {
  const {
    VEHICLE_ENDPOINT,
    vehicleModel,
    setVehicleModel,
    vehicleData,
    config,
    pageIndexVehicle,
    setPageIndexVehicle,
    toggleVisibleModalConfirm,
    createEntity,
    updateEntity,
    deleteEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const [vehicleDataFiltered, setVehicleDataFiltered] = useState(
    vehicleData.Data
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getVehicleFilteringOptions());
  }, []);

  const validateOperationResult = (result, isDelete) => {
    setVehicleModel(getClearVehicleModel());
    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([VEHICLE_ENDPOINT, config]);
    if (isDelete) {
      if (pageIndexVehicle !== 0 && vehicleData.Data.length === 1) {
        setPageIndexVehicle(pageIndexVehicle - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${VEHICLE_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${VEHICLE_ENDPOINT}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${VEHICLE_ENDPOINT}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  useEffect(() => {
    setVehicleDataFiltered(vehicleData.Data);
  }, [vehicleData]);

  const disableSaveChanges = () =>
    !vehicleModel?.Identification ||
    vehicleModel?.Identification.replaceAll(" ", "").length <= 0 ||
    vehicleModel?.VehicleTypeId <= 0;

  const getColumnTitles = () => {
    return [
      localize.vehicle_type,
      localize.identification,
      localize.soat,
      localize.technomechanic_review,
      localize.assigned_to,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.vehicles}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={VEHICLE_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="vehicle"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_vehicle}
          onClickToSubmit={async () => {
            await handleUpdateRequest(vehicleModel);
            setShowEditModal(false);
            setVehicleModel(getClearVehicleModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setVehicleModel(getClearVehicleModel());
          }}
          Component={<EditVehicle />}
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.vehicle}
            subtitle={localize.vehicle_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => {
              setShowPrinterModal(true);
            }}
            entityName="vehicle_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_vehicle}
                onClickToSubmit={async () => {
                  await handleCreateRequest(vehicleModel);
                  setShowAddModal(false);
                  setVehicleModel(getClearVehicleModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setVehicleModel(getClearVehicleModel());
                }}
                Component={<AddVehicle />}
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      <DeleteConfirmationMessage
        entityName={`${vehicleModel?.VehicleType?.Name} (${vehicleModel?.Identification})`}
        entityType={localize.vehicle}
        onClickForCancel={() => {
          toggleVisibleModalConfirm();
          setVehicleModel(getClearVehicleModel());
        }}
        onClickForConfirm={() => {
          handleDeleteRequest(vehicleModel.Id);
        }}
      />

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexVehicle}
            setPageIndex={setPageIndexVehicle}
            dataArray={vehicleData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          vehicleData.Data?.length !== 0
            ? vehicleDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setVehicleModel({
                          ...item,
                          Personal: item?.AssignedTo,
                          PersonalId: item?.AssignedTo?.id,
                          VehicleTypeId: item?.VehicleType.Id,
                          Soat: new Date(item.Soat).toISOString().split("T")[0],
                          TechnicalReview: new Date(item.TechnicalReview)
                            .toISOString()
                            .split("T")[0],
                        });
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setVehicleModel(item);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.VehicleType.Name,
                  item.Identification,
                  GetDateInMMDDYYYYFormat(new Date(item.Soat)),
                  GetDateInMMDDYYYYFormat(new Date(item.TechnicalReview)),
                  !item.AssignedTo ? (
                    <NoDataLabel />
                  ) : (
                    `${item.AssignedTo.SurName} ${item.AssignedTo.LastName}`
                  ),
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(7)]
        }
      />
    </div>
  );
}
